import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Briefcase, Calendar, DollarSign, CreditCard, FileText, Building2, LucideIcon } from 'lucide-react';
import { useLanguage } from 'contexts/LanguageContext';

interface Project {
  id: number;
  icon?: string;
  title: string;
  description: string;
  category: string;
  timeline: string | null;
  company?: string;
  image: string;
  techStack: string[];
  liveLink: string;
  githubLink: string;
  featured: boolean;
}

const ICON_MAP: Record<string, LucideIcon> = {
  DollarSign,
  CreditCard,
  FileText,
  Building2,
  Calendar,
  Briefcase,
};

const Projects: React.FC = () => {
  const { t } = useLanguage();
  const projects: Project[] = t.projects;
  const featuredProjects = projects.filter((p) => p.featured);
  const otherProjects = projects.filter((p) => !p.featured);

  const getProjectIcon = (project: Project) => ICON_MAP[project.icon ?? ''] ?? Briefcase;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            {t.projectsSection.badge}
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            {t.projectsSection.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {t.projectsSection.description}
          </p>
        </div>

        {/* Featured Projects */}
        <div className="space-y-12 mb-16">
          {featuredProjects.map((project, index) => (
            <Card
              key={project.id}
              className={`overflow-hidden hover:shadow-xl transition-shadow ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } flex flex-col lg:flex`}
            >
              <div className="lg:w-1/2">
                <div className="relative h-64 lg:h-full overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center group">
                  {React.createElement(getProjectIcon(project), {
                    className: "w-32 h-32 text-primary/60 transition-all duration-500 group-hover:scale-110 group-hover:text-primary"
                  })}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              <div className="lg:w-1/2 flex flex-col justify-between">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="default" className="text-xs">
                          <Briefcase className="w-3 h-3 mr-1" />
                          {project.category}
                        </Badge>
                        {project.timeline && (
                          <Badge variant="secondary" className="text-xs">
                            <Calendar className="w-3 h-3 mr-1" />
                            {project.timeline}
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-2xl mb-2">{project.title}</CardTitle>
                      {project.company && (
                        <p className="text-sm text-primary mb-2 font-medium">
                          {project.company}
                        </p>
                      )}
                      <CardDescription className="text-base">{project.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {project.techStack.map((tech) => (
                      <Badge key={tech} variant="outline" className="text-xs">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>

        {/* Other Projects Grid */}
        {otherProjects.length > 0 && (
          <>
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8">
              {t.projectsSection.internshipProjects}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {otherProjects.map((project) => {
                const ProjectIcon = getProjectIcon(project);
                return (
                <Card key={project.id} className="flex flex-col hover:shadow-lg transition-shadow">
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center group">
                    <ProjectIcon className="w-20 h-20 text-primary/60 transition-all duration-500 group-hover:scale-110 group-hover:text-primary" />
                  </div>
                  <CardHeader className="flex-grow">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge variant="default" className="text-xs">
                        <Briefcase className="w-3 h-3 mr-1" />
                        {project.category}
                      </Badge>
                      {project.timeline && (
                        <Badge variant="secondary" className="text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          {project.timeline}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl">{project.title}</CardTitle>
                    {project.company && (
                      <p className="text-sm text-primary font-medium mt-1">
                        {project.company}
                      </p>
                    )}
                    <CardDescription className="mt-2">{project.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.slice(0, 4).map((tech) => (
                        <Badge key={tech} variant="secondary" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                      {project.techStack.length > 4 && (
                        <Badge variant="secondary" className="text-xs">
                          +{project.techStack.length - 4}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Projects;

